import { Commodity } from "@/pages/sy/models/Commodity";
import {
  Alert,
  Box,
  Button,
  Dialog,
  Divider,
  List,
  ListItem,
  ListItemButton,
  Typography,
} from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import { useTheme } from "@mui/material/styles";
import { chargeOperationStyles } from "@/pages/sy/styles/ChargeOperationStyles";

interface OriginalRecipeCommodity {
  commodityId: number;
  commodityName: string;
  commodityColor: string | null;
}

interface CommodityModalProps {
  commodities: Commodity[];
  isCommodityDialogOpen: boolean;
  onClose: () => void;
  selectedCommodityId: number | null;
  setSelectedCommodityId: (selectedCommodityId: number | null) => void;
  handleCommoditySelect: (commodityId: number) => void;
  hasSubstitutes: boolean;
  originalRecipeCommodity?: OriginalRecipeCommodity | null;
}

export const CommodityModal = ({
  commodities,
  isCommodityDialogOpen,
  onClose,
  selectedCommodityId,
  setSelectedCommodityId,
  handleCommoditySelect,
  hasSubstitutes,
  originalRecipeCommodity,
}: CommodityModalProps) => {
  const theme = useTheme();
  const styles = chargeOperationStyles(theme);

  return (
    <Dialog
      open={isCommodityDialogOpen}
      onClose={onClose}
      maxWidth="md"
      slotProps={{
        paper: {
          sx: styles.dialogPaper,
        },
      }}
    >
      <Box sx={styles.dialogHeader}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Select Commodity
        </Typography>
      </Box>

      <Box sx={styles.dialogContent}>
        {!hasSubstitutes && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No commodity suggested for substitution
          </Alert>
        )}

        {originalRecipeCommodity && (
          <>
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  sx={{
                    ...(styles.recipeItem as object),
                    ...(originalRecipeCommodity.commodityId ===
                    selectedCommodityId
                      ? (styles.selectedRecipeItem as object)
                      : {}),
                    backgroundColor: theme.palette.action.hover,
                  }}
                  onClick={() =>
                    setSelectedCommodityId(originalRecipeCommodity.commodityId)
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <RestoreIcon
                      sx={{ mr: 1, color: theme.palette.primary.main }}
                    />
                    <Box
                      sx={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "4px",
                        marginRight: 2,
                        backgroundColor:
                          originalRecipeCommodity.commodityColor ||
                          theme.palette.grey[600],
                      }}
                    />
                    <Typography sx={styles.recipeText}>
                      Revert to: {originalRecipeCommodity.commodityName}
                    </Typography>
                  </Box>
                </ListItemButton>
              </ListItem>
            </List>
            <Divider sx={{ my: 1 }} />
          </>
        )}

        <List>
          {commodities.map((commodity) => (
            <ListItem key={commodity.CommodityId} disablePadding>
              <ListItemButton
                sx={{
                  ...(styles.recipeItem as object),
                  ...(commodity.CommodityId === selectedCommodityId
                    ? (styles.selectedRecipeItem as object)
                    : {}),
                }}
                onClick={() =>
                  setSelectedCommodityId(commodity.CommodityId as number)
                }
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Box
                    sx={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "4px",
                      marginRight: 2,
                      backgroundColor:
                        commodity.CommodityColor || theme.palette.grey[600],
                    }}
                  />
                  <Typography sx={styles.recipeText}>
                    {commodity.CommodityName}
                  </Typography>
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      <Box
        sx={{ ...(styles.buttonsContainer as object), display: "flex", pb: 2 }}
      >
        <Button
          variant="contained"
          color="inherit"
          onClick={onClose}
          sx={styles.actionButton}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (selectedCommodityId) {
              handleCommoditySelect(selectedCommodityId);
            }
          }}
          sx={styles.actionButton}
          disabled={!selectedCommodityId}
        >
          Select
        </Button>
      </Box>
    </Dialog>
  );
};
